'use strict';

import extend from 'lodash/extend';

/**
 * Domain Object Service.
 */

(function() {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.domain = window.atex.aceui.domain || {};

  atex.aceui.domain.DomainObjectService = function(Q, FileDeliveryService) {
    var DOMAIN_OBJECT_EXTERNAL_ID_PREFIX = '_domainObject/';
    var DEFAULT_DOMAIN_OBJECT_ID = 'DefaultDomainObject';

    var domainObjectDefinitionLoadCache = {};

    var register = function(type, domainObjectId, domainObjectRequires, domainObjectFactoryFn) {
      var domainObjectDefinition = domainObjectDefinitionLoadCache[domainObjectId];

      if (typeof domainObjectDefinition === 'undefined') {
        // This domain object does not exist, and nobody is waiting for it either.
        // NOTE: this basically only addresses cases we don't really support yet.

        var deferred = Q.defer();

        deferred.resolve({ type: type, name: domainObjectId, artifact: domainObjectFactoryFn });
        domainObjectDefinitionLoadCache[domainObjectId] = deferred.promise;
      } else if (typeof domainObjectDefinition.resolve !== 'undefined') {
        // The domain object does not exist yet, but someone else is waiting for it.

        var deferred = domainObjectDefinition;

        deferred.resolve({ type: type, name: domainObjectId, artifact: domainObjectFactoryFn });
        domainObjectDefinitionLoadCache[domainObjectId] = deferred.promise;
      }
    };

    var loadDomainObjectDefinition = function(domainObjectDefinitionId, typeName) {
      var contentId = DOMAIN_OBJECT_EXTERNAL_ID_PREFIX + domainObjectDefinitionId;

      return FileDeliveryService.getTextFile({ alias: contentId, fileName: 'domain-object.js' }).then(function(result) {
        var domainObjectCode = result.text;
        var sharedThis = {};
        var scopedAtex = extend({}, atex);

        scopedAtex.aceui = extend({}, scopedAtex.aceui);
        scopedAtex.aceui.register = register.bind({});

        try {
          var codeRunner = new Function('window', 'atex', '"use strict";' + domainObjectCode);
          codeRunner.call(sharedThis, window, scopedAtex);
          return Q.when();
        } catch (error) {
          var errorMessage = "Error while loading domain object '" + domainObjectDefinitionId + "'";
          errorMessage += (typeof typeName !== 'undefined') ? " for type '" + typeName + "'." : '.';

          var e = atex.aceui.error.Error({
            origin: 'atex.aceui.domain.DomainObjectService',
            type: atex.aceui.error.types.UNKNOWN,

            rootCause: error,
            cause: errorMessage
          });

          domainObjectDefinitionLoadCache[domainObjectDefinitionId].reject(e);

          return Q.reject(e);
        }
      }).then(null, function(error) {
        var domainObjectDefinitionLoadObject = domainObjectDefinitionLoadCache[domainObjectDefinitionId];

        if (typeof domainObjectDefinitionLoadObject !== 'undefined' && typeof domainObjectDefinitionLoadObject.resolve !== 'undefined') {
          // In case the Domain Object for the given type name doesn't exist, we fallback to a default domain object

          if (typeof error.type !== 'undefined' && error.type === atex.aceui.error.types.NOT_FOUND) {
            var defaultDefinition = {
              type: 'domain-object',
              name: DEFAULT_DOMAIN_OBJECT_ID,
              artifact: function() { return {}; }
            };

            domainObjectDefinitionLoadObject.resolve(defaultDefinition);
            domainObjectDefinitionLoadCache[domainObjectDefinitionId] = domainObjectDefinitionLoadObject.promise;

            return;
          }

          domainObjectDefinitionLoadObject.reject(error);
        }
      });
    };

    return {
      getDomainObjectDefinition: function(domainObjectDefinitionId, typeName, arrayTypeName) {
        var domainObjectDefinition = domainObjectDefinitionLoadCache[domainObjectDefinitionId];
        var loadPromise = undefined;

        if (typeof domainObjectDefinition === 'undefined') {
          // The domain object doesn't exist in the cache yet at all.
          // We create a new load promise for it.

          var deferred = Q.defer();

          domainObjectDefinitionLoadCache[domainObjectDefinitionId] = deferred;
          loadPromise = deferred.promise;

          loadDomainObjectDefinition(domainObjectDefinitionId, typeName);

          setTimeout(function() {
            var domainObjectDefinitionLoadObject = domainObjectDefinitionLoadCache[domainObjectDefinitionId];

            if (typeof domainObjectDefinitionLoadObject !== 'undefined' && typeof domainObjectDefinitionLoadObject.resolve !== 'undefined') {
              delete domainObjectDefinitionLoadCache[domainObjectDefinitionId];

              var errorMessage = "Timeout while loading domain object '" + domainObjectDefinitionId + "'";
              errorMessage += (typeof typeName !== 'undefined') ? " for type '" + typeName + "'." : '.';

              var error = atex.aceui.error.Error({
                origin: 'atex.aceui.domain.DomainObjectService',
                type: atex.aceui.error.types.TIMEOUT,

                cause: errorMessage
              });

              domainObjectDefinitionLoadObject.reject(error);
            }
          }, 30000);
        } else if (typeof domainObjectDefinition.resolve !== 'undefined') {
          // The domain object does not exist, but it's already been requested by someone else.
          // We simply re-use the load promise created by the first request.

          loadPromise = domainObjectDefinition.promise;
        } else {
          // The domain object already exists in the cache, fully loaded.
          // We simply use the result.

          loadPromise = domainObjectDefinition;
        }

        return loadPromise.then(function(definitionResult) {
          var definitionObject = undefined;

          try {
            definitionObject = definitionResult.artifact();
          } catch (error) {
            var errorMessage = "Error instantiating domain object '" + domainObjectDefinitionId + "'";
            errorMessage += (typeof typeName !== 'undefined') ? " for type '" + typeName + "'." : '.';

            var e = atex.aceui.error.Error({
              origin: 'atex.aceui.domain.DomainObjectService',
              type: atex.aceui.error.types.UNKNOWN,

              rootCause: error,
              cause: errorMessage
            });

            return Q.reject(e);
          }

          var domainObjectId = (typeof definitionResult !== 'undefined' && definitionResult.name === DEFAULT_DOMAIN_OBJECT_ID) ?
            DEFAULT_DOMAIN_OBJECT_ID : domainObjectDefinitionId;

          var domainObjectDefinition = Object.create(atex.aceui.domain.DomainObjectDefinition(domainObjectId, typeName, arrayTypeName));
          extend(domainObjectDefinition, (definitionObject.definition || {}));

          var customDefinition = extend({}, (definitionObject || {}));
          delete customDefinition.definition;

          domainObjectDefinition.customDefinition = customDefinition;

          Object.seal(domainObjectDefinition);

          return domainObjectDefinition;
        });
      }
    };
  };
})();
